import { HandCoins, PieChart } from "lucide-react";
import { Badge, Card, Notice, SectionHeading } from "./ui";
import type { SurplusResult } from "../lib/api";
import { formatINRExact } from "../lib/format";

const POLICY_LABELS: Record<string, string> = {
  share_ratio: "Share ratio (refund by contribution share)",
  cohort: "entire enrolled cohort (seniors + juniors)",
  contributors: "contributing students only",
  attendees: "actual attendees only",
  custom: "a custom confirmed list",
};

export default function SurplusCalculator({
  surplus,
  seniorsCount,
  juniorsCount,
}: {
  surplus: SurplusResult;
  seniorsCount: number;
  juniorsCount: number;
}) {
  return (
    <section id="surplus" className="scroll-mt-24 bg-[#050505] py-24">
      <div className="mx-auto max-w-shell px-5 md:px-10">
        <SectionHeading
          eyebrow="After the party"
          title="Remaining money, split fairly"
          description="Once the event is done, any surplus is calculated strictly from recorded actual income and actual expenses — never from projections. The leftover is returned to the students by their contribution-share ratio."
        />

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="p-6">
            <div className="flex items-center gap-2 text-sm font-medium text-wandor-muted">
              <PieChart className="h-4 w-4 text-wandor-prompt" aria-hidden="true" />
              Actual income received
            </div>
            <p className="mt-2 text-2xl font-semibold text-white">
              {formatINRExact(surplus.actualIncomePaise)}
            </p>
            <p className="mt-1 text-sm text-wandor-muted">Confirmed receipts only</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 text-sm font-medium text-wandor-muted">
              <PieChart className="h-4 w-4 text-wandor-prompt" aria-hidden="true" />
              Actual expenses recorded
            </div>
            <p className="mt-2 text-2xl font-semibold text-white">
              {formatINRExact(surplus.actualExpensePaise)}
            </p>
            <p className="mt-1 text-sm text-wandor-muted">Bills and receipts entered by the committee</p>
          </Card>

          <Card className={`p-6 ${surplus.isDeficit ? "border-red-400/30" : "border-emerald-400/30"}`}>
            <div className="flex items-center gap-2 text-sm font-medium text-wandor-muted">
              <HandCoins className="h-4 w-4 text-wandor-prompt" aria-hidden="true" />
              {surplus.isDeficit ? "Deficit" : "Surplus"}
            </div>
            <p className="mt-2 text-2xl font-semibold text-white">
              {formatINRExact(Math.abs(surplus.surplusPaise))}
            </p>
            <p className="mt-1 text-sm text-wandor-muted">
              {surplus.isDeficit
                ? "Expenses exceed income — no distribution is calculated."
                : "Actual income − Actual expenses"}
            </p>
          </Card>
        </div>

        <Card className="mt-6 p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-xl font-semibold text-white">Distribution estimate</h3>
            <Badge tone="accent">Policy: {POLICY_LABELS[surplus.eligiblePolicy] ?? surplus.eligiblePolicy}</Badge>
            {!surplus.isDeficit && surplus.eligibleCount > 0 && (
              <Badge tone="neutral">
                {surplus.eligibleCount} eligible students ({seniorsCount} seniors + {juniorsCount} juniors
                currently enrolled)
              </Badge>
            )}
          </div>

          {surplus.isDeficit ? (
            <div className="mt-5">
              <Notice tone="warning">
                The event currently has a recorded deficit of {formatINRExact(Math.abs(surplus.surplusPaise))}.
                No positive distribution is calculated. If income records are not yet complete, enter the
                actual receipts in the administrator panel and this figure will update.
              </Notice>
            </div>
          ) : surplus.eligibleCount <= 0 ? (
            <div className="mt-5">
              <Notice tone="warning">
                No eligible students are configured for distribution. The committee must confirm the
                eligibility rule and counts in the administrator panel.
              </Notice>
            </div>
          ) : surplus.surplusPaise === 0 ? (
            <div className="mt-5">
              <Notice tone="info">
                Actual income exactly matches actual expenses — there is nothing to distribute yet.
              </Notice>
            </div>
          ) : (
            <>
              {surplus.shareRatio ? (
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <Card className="p-5">
                    <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">
                      Distributable amount
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-white">
                      {formatINRExact(surplus.distributablePaise)}
                    </p>
                    <p className="mt-1 text-xs text-wandor-muted">
                      Returned to students by contribution share
                    </p>
                  </Card>
                  <Card className="border-emerald-400/30 p-5">
                    <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">
                      Per senior student
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-emerald-300">
                      {formatINRExact(surplus.shareRatio.seniorPerStudentPaise)}
                    </p>
                    <p className="mt-1 text-xs text-wandor-muted">
                      Senior share {surplus.shareRatio.seniorShareBps / 100}% (₹1,290 ÷ ₹2,080)
                    </p>
                  </Card>
                  <Card className="border-emerald-400/30 p-5">
                    <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">
                      Per junior student
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-emerald-300">
                      {formatINRExact(surplus.shareRatio.juniorPerStudentPaise)}
                    </p>
                    <p className="mt-1 text-xs text-wandor-muted">
                      Junior share {surplus.shareRatio.juniorShareBps / 100}% (₹790 ÷ ₹2,080)
                    </p>
                  </Card>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <Card className="p-5">
                    <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">
                      Distributable amount
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-white">
                      {formatINRExact(surplus.distributablePaise)}
                    </p>
                  </Card>
                  <Card className="border-emerald-400/30 p-5">
                    <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">
                      Estimated amount per student
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-emerald-300">
                      {formatINRExact(surplus.perStudentPaise)}
                    </p>
                    <p className="mt-1 text-xs text-wandor-muted">
                      Equal share, rounded down to whole paise
                    </p>
                  </Card>
                  <Card className="p-5">
                    <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">
                      Rounding residual
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-white">
                      {formatINRExact(surplus.residualPaise)}
                    </p>
                    <p className="mt-1 text-xs text-wandor-muted">
                      Paise that cannot be split equally — held by the committee
                    </p>
                  </Card>
                </div>
              )}

              <div className="mt-5">
                <Notice tone="info">
                  {surplus.shareRatio ? (
                    <>
                      Calculation: surplus is split by contribution share — each senior student receives{" "}
                      {formatINRExact(surplus.shareRatio.seniorPerStudentPaise)} and each junior student{" "}
                      {formatINRExact(surplus.shareRatio.juniorPerStudentPaise)} (rounded down to whole paise;{" "}
                      {formatINRExact(surplus.residualPaise)} remains as rounding residual). This is an
                      estimate only — the committee confirms the final figures before any money is returned.
                      No transfers are initiated by this website.
                    </>
                  ) : (
                    <>
                      Calculation: {formatINRExact(surplus.surplusPaise)} ÷ {surplus.eligibleCount} eligible
                      students = {formatINRExact(surplus.perStudentPaise)} each (rounded down to whole paise;
                      {formatINRExact(surplus.residualPaise)} remains as rounding residual). This is an
                      estimate only — the committee confirms the final eligibility policy before any money is
                      returned. No transfers are initiated by this website.
                    </>
                  )}
                </Notice>
              </div>
            </>
          )}
        </Card>
      </div>
    </section>
  );
}
